import { evolve } from 'remeda';

import {
    type NewUnityUser,
    type NewUser,
    type UnityUser,
    type User,
} from '@@api/services/auth/schemas';
import { DEFAULT_TENANT_ID } from '@@containers/metadata/constants';
import deserializeState from '@@plate/serialization/unity/utils/deserializeState';
import serializeState from '@@plate/serialization/unity/utils/serializeState';

import { deserializeVariants, serializeVariants } from '../../utils/transformers';

type FullNameAndInitials = { initials?: string; fullName?: string };

export const getFullName = (user?: { firstName?: string | null; lastName?: string | null }) =>
    `${user?.firstName || ''} ${user?.lastName || ''}`.trim();

export const getFullNameAndInitials = (user: UnityUser): FullNameAndInitials => {
    if (user.firstNameFromAD === null || user.lastNameFromAD === null) {
        return {};
    }

    const initials = `${user.firstNameFromAD.charAt(0)}${user.lastNameFromAD.charAt(0)}`;

    let firstName = '';
    let lastName = '';

    if (user.serviceUser) {
        firstName = user.firstNameFromAD;
    } else {
        firstName = user.variants[DEFAULT_TENANT_ID]?.firstName ?? '';
        lastName = user.variants[DEFAULT_TENANT_ID]?.lastName ?? '';
    }

    return {
        initials,
        fullName: getFullName({ firstName, lastName }),
    };
};

export const deserializeUser = (entity: UnityUser): User => {
    const deserializedVariants = deserializeVariants(entity.variants, (variant) =>
        evolve(variant, {
            shortBiography: deserializeState,
            longBiography: deserializeState,
            userImage: {
                caption: deserializeState,
            },
        }),
    );

    return {
        ...entity,
        variants: deserializedVariants,
        ...getFullNameAndInitials(entity),
    };
};

export const serializeUser = (entity: User | NewUser): UnityUser | NewUnityUser => {
    const serializedVariants = serializeVariants(entity.variants, (variant) =>
        evolve(variant, {
            shortBiography: serializeState,
            longBiography: serializeState,
            userImage: {
                caption: serializeState,
            },
            prolitterisId: (value) => (value && value?.length > 0 ? value : null),
        }),
    );

    return {
        firstNameFromAD: null,
        lastNameFromAD: null,
        username: null,
        ...entity,
        variants: serializedVariants,
    };
};
